import { CurrencyPipe, NgFor, NgIf } from '@angular/common'
import { HttpParams } from '@angular/common/http'
import { Component, ViewChild } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { WjChartModule } from '@grapecity/wijmo.angular2.chart'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { CellType, GridPanel } from '@grapecity/wijmo.grid'
import { Menu } from '@grapecity/wijmo.input'
import { NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase } from '@ng-bootstrap/ng-bootstrap'

import { BaseComponent, FeatureType, Grid, ListItem } from '../../shared'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { GridSelectionCalculationComponent } from '../../shared/reusable-components/grid-selection-calculation/grid-selection-calculation.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'

@Component({
    templateUrl: './fte-projections.component.html',
    styleUrls: ['./fte-projections.component.css'],
    standalone: true,
    imports: [
        NgbNav,
        NgbNavItem,
        NgbNavItemRole,
        NgbNavLink,
        NgbNavLinkBase,
        FormsModule,
        ActionToolbarCount,
        GridExportComponent,
        WjInputModule,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
        NgFor,
        GridSelectionCalculationComponent,
        NgIf,
        WjChartModule,
    ],
})
export class FteProjectionsComponent extends BaseComponent {
    @ViewChild('ctxMenu') ctxMenu!: Menu
    menuItems = [] as any[]
    currencyPipe = new CurrencyPipe('en-US')
    minDate = new Date(2014, 0, 1)
    maxDate = new Date(2024, 6, 1)

    startDate = new Date(2023, 0, 1)
    endDate = new Date(2024, 6, 1)

    faSpinner = faSpinner
    active = 1
    scrollPosition: any
    activeJobsOnly = true
    openMonth!: Date
    monthlyHours1 = 220
    monthlyHours2 = 173
    boolMap: ListItem<boolean>[] = [new ListItem(false, 'Costs'), new ListItem(true, 'Revenue')]
    isRevenue = false

    // FTE Projection Properties
    projectedRevenueGrid: Grid<any>
    earnedRevenueGrid: Grid<any>
    jobCostDetailsGrid: Grid<any>
    prDeptGrid: Grid<any>
    jccdGrid: Grid<any>
    fteGrid: Grid<any>
    fteMonthCols = [] as string[]
    lastMonthData = [] as any[]

    chartData!: any

    get gridHeader() {
        switch (this.active) {
            case 1:
                return 'Overhead FTE Projections'
            case 2:
                return 'Job Type FTE Projections'
            case 5:
                return 'JC Class FTE Projections'
            case 3:
                return 'Overhead FTE Actuals'
            case 4:
                return 'PR FTE Historical Chart'
            case 7:
                return 'JC Class FTE Actuals'
            case 6:
                return 'Job Type FTE Actuals'
            default:
                return ''
        }
    }

    get colHeader() {
        switch (this.active) {
            case 1:
            case 3:
                return 'Department'
            case 2:
            case 6:
                return 'Job Type'
            case 5:
            case 7:
                return 'Class'
            default:
                return ''
        }
    }

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.UsersEdit])
    }

    constructor() {
        super()

        this.jobCostDetailsGrid = new Grid<any>(this.services, 'id', this).wrapColumnHeaders()
        this.prDeptGrid = new Grid<any>(this.services, 'id', this).wrapColumnHeaders()
        this.jccdGrid = new Grid<any>(this.services, 'id', this).wrapColumnHeaders()
        this.fteGrid = new Grid<any>(this.services, 'id', this).wrapColumnHeaders()
        this.earnedRevenueGrid = new Grid<any>(this.services, 'id', this).wrapColumnHeaders()
        this.projectedRevenueGrid = new Grid<any>(this.services, 'id', this).wrapColumnHeaders()

        this.refreshDates()
        this.refresh()
    }

    refreshDates() {
        this.services.data.get<Date>('/api/FteProjections/GetTimeExpenseEndDate').subscribe({
            next: successResult => {
                const date = new Date(successResult)
                const end = new Date(date.getFullYear(), date.getMonth(), 1)
                this.endDate = new Date(end)
                this.maxDate = new Date(end)
                this.startDate = new Date(end.setMonth(end.getMonth() - 18))
            },
        })
    }

    updateData() {
        this.services.data.load(true, false, this.services.data.get<Date>('/api/FteProjections/UpdateData')).subscribe({
            next: () => {
                this.refreshDates()
                this.services.notification.success('The data was updated successfully')
            },
            error: () => this.services.notification.error('Update failed'),
        })
    }

    refresh() {
        this.cancelableRequest(
            '',
            true,
            this.services.data
                .load(true, true, this.services.data.vpJobs.queryAll(), this.services.data.get('/api/Projections/GetOpenMonth'))
                .subscribe({
                    next: result => {
                        this.initialLoadingComplete()
                        this.openMonth = new Date(result[1])

                        this.refreshFteData()
                    },
                }),
        )
    }

    refreshFteData() {
        this.fteGrid.items = []
        this.prDeptGrid.items = []
        this.earnedRevenueGrid.items = []
        this.projectedRevenueGrid.items = []

        const params = new HttpParams()
            .set('startDate', this.startDate.toLocaleDateString())
            .set('endDate', this.endDate.toLocaleDateString())
            .set('monthlyHours1', this.monthlyHours1)
            .set('monthlyHours2', this.monthlyHours2)
            .set('isRevenueBased', this.isRevenue)

        // We get different data depending on the active tab
        let query
        switch (this.active) {
            case 1:
                query = this.services.data.get('/api/FteProjections/GetOverheadFteForecast', params)
                break
            case 2:
                query = this.services.data.get('/api/FteProjections/GetFteForecastByJobType', params)
                break
            case 5:
                query = this.services.data.get('/api/FteProjections/GetFteForecastByClass', params)
                break
            case 3:
                query = this.services.data.get('/api/FteProjections/GetOverheadFteHistorical', params)
                break
            case 4:
                query = this.services.data.get('/api/FteProjections/GetPrFteHistoricalChart', params)
                break
            case 6:
                query = this.services.data.get('/api/FteProjections/GetJobTypeFteHistorical', params)
                break
            case 7:
                query = this.services.data.get('/api/FteProjections/GetClassFteHistorical', params)
                break
            case 8:
                query = this.services.data.get('/api/FteProjections/GetPRForecastByPositionCode', params)
                break

            default:
        }

        this.cancelableRequest(
            'FteProjection',
            true,
            this.services.data.load(true, true, query).subscribe({
                next: result => {
                    if (this.active === 4) {
                        this.chartData = result[0]
                    } else {
                        this.prDeptGrid.items = result[0].prDepartmentData
                        this.jccdGrid.items = result[0].jobCostDetailsData
                        this.earnedRevenueGrid.items = result[0].earnedRevenue
                        if (result[0].lastMonthData) {
                            this.lastMonthData = result[0].lastMonthData
                        }

                        // Add the job types to the validation data
                        const projRev = result[0].projectedRevenue
                        for (const rev of projRev) {
                            rev.jobType =
                                this.services.data.vpJobs.items.find(x => x.id.trim() == rev.jobNum.trim())?.jobType ??
                                '*Job Type Not Found'
                        }
                        this.projectedRevenueGrid.items = projRev

                        this.generateFteColumns(result[0].reportData)
                    }
                },
            }),
        )
    }

    generateFteColumns(fteData) {
        this.fteMonthCols = []
        setTimeout(() => {
            const cols = [] as any[]
            const uniqueDates = [...new Set(fteData.map(item => this.dateToColHeader(item.month)))]

            let years = 1
            for (let i = 0; i < uniqueDates.length; i++) {
                cols.push(uniqueDates[i])
                if ((i + 1) % 12 === 0 && i != 0) {
                    cols.push(12 * years + '-Month Avg Totals')
                    if (i + 1 === 12) {
                        cols.unshift(12 * years + '-Month Avg Totals')
                    }
                    years++
                }
            }
            this.fteMonthCols = cols
            this.generateFteRows(fteData)
        }, 10)
    }

    generateFteRows(fteData) {
        const rows = [] as any[]
        const dataByDeparment = fteData.toObjectGroups(
            x => x.department,
            x => x,
        )
        const nonTotalCols = this.fteMonthCols.filter(x => x.indexOf('Avg') == -1)

        let longestData = 0
        for (const prop in dataByDeparment) {
            const departmentData = dataByDeparment[prop]
            if (departmentData.length > longestData) {
                longestData = departmentData.length
            }
        }

        for (const prop in dataByDeparment) {
            const departmentData = dataByDeparment[prop] as any[]
            const monthFtes = {} as any
            let years = 1
            let yearTotal = 0

            for (let i = 0; i < longestData; i++) {
                const key = nonTotalCols[i]
                const match = departmentData.find(x => this.dateToColHeader(x.month) === key)
                let fte = 0
                if (match) {
                    fte = match.fte
                }
                monthFtes[key] = fte
                yearTotal += fte
                if ((i + 1) % 12 === 0 && i != 0) {
                    const totalKey = 12 * years + '-Month Avg Totals'
                    monthFtes[totalKey] = yearTotal / 12
                    years++
                    yearTotal = 0
                }
            }

            rows.push({
                department: prop.trim(),
                fteByMonth: monthFtes,
                monthlyHours: departmentData[0].monthlyHours,
                deptDescription: departmentData[0].deptDescription,
                lastMonth: this.lastMonthData.find(x => x.department == prop)?.fte ?? 0,
            })
        }

        // Finally to a totals of all columns
        const fteByMonth = {}
        for (const row of rows) {
            for (const prop in row.fteByMonth) {
                const val = row.fteByMonth[prop]
                if (prop in fteByMonth) {
                    fteByMonth[prop] += val
                } else {
                    fteByMonth[prop] = val
                }
            }
        }
        rows.push({
            deptDescription: 'Totals',
            fteByMonth: fteByMonth,
        })

        this.fteGrid.items = rows
    }

    fteGridItemFormatter = (panel: GridPanel, r, c, cell: HTMLDivElement) => {
        if (panel.cellType === CellType.Cell) {
            const row = panel.grid.rows[r]
            const col = panel.grid.columns[c] as any
            const rowItem = row.dataItem
            cell.style.fontWeight = 'unset'
            if (col.binding == 'job' && !rowItem.detailsByMonth) {
                cell.style.fontWeight = '700'
            }
            if (col.binding.indexOf('Totals') > -1) {
                cell.style.fontWeight = '700'
            }
            if (rowItem.deptDescription && rowItem.deptDescription.indexOf('Totals') > -1) {
                cell.style.fontWeight = '700'
            }
        }
    }

    dateToColHeader(date: Date) {
        return this.monthByInt.find(x => x.val == date.getMonth())?.display + ' ' + (date.getFullYear() - 2000)
    }

    tabChanged() {
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'))
            this.refreshFteData()
        }, 50)
    }

    // Make it easy to lookup the month format we have chosen for our column binding (e.g. 'Jan 23')
    monthByInt = [
        { val: 0, display: 'Jan' },
        { val: 1, display: 'Feb' },
        { val: 2, display: 'Mar' },
        { val: 3, display: 'Apr' },
        { val: 4, display: 'May' },
        { val: 5, display: 'Jun' },
        { val: 6, display: 'Jul' },
        { val: 7, display: 'Aug' },
        { val: 8, display: 'Sep' },
        { val: 9, display: 'Oct' },
        { val: 10, display: 'Nov' },
        { val: 11, display: 'Dec' },
    ]
}
